import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import './index.css';
import 'src/libs/sentry';

import '@fontsource/nunito-sans';
import '@fontsource-variable/instrument-sans';

import { App } from './app';
import { AppContextProvider } from './contexts/AppContext/AppContextProvider';
import { AuthContextProvider } from './contexts/AuthContext/AuthContextProvider';
import { ThemePreferenceProvider } from './contexts/ThemePreferenceContext/ThemePreferenceProvider';
import theme from './theme';

const queryClient = new QueryClient();

const container = document.getElementById('root');
const root = createRoot(container!);

/**
 * A note on the provider order:
 * AuthContextProvider is responsible for fetching & refreshing JWT
 * The JWT is required in the QueryClientProvider, this must be a child of the AuthContextProvider
 * The AppContextProvider uses queries from the QueryClientProvider and requires information from the AuthContextProvider.
 * The FilterContextProvider uses queries from the QueryClientProvider and data from the AppContextProvider.
 */
root.render(
  <React.StrictMode>
    <ColorModeScript initialColorMode={theme.config.initialColorMode} />
    <ChakraProvider
      theme={theme}
      toastOptions={{
        defaultOptions: {
          duration: 5000,
          isClosable: true,
          variant: 'solid',
          // Colors come from the Toast component theme (color-mode aware);
          // only mode-independent sizing/typography belongs here.
          containerStyle: {
            borderRadius: '6px',
            boxShadow: 'lg',
            fontSize: '14px',
            fontWeight: '500',
          },
        },
      }}>
      <ThemePreferenceProvider>
        <AuthContextProvider>
          <QueryClientProvider client={queryClient}>
            <AppContextProvider>
              <App />
            </AppContextProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </AuthContextProvider>
      </ThemePreferenceProvider>
    </ChakraProvider>
  </React.StrictMode>,
);
